import ChangeCalculator from '../ChangeCalculator/ChangeCalculator';
import { LocalAtm, CreditCard } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './PaymentMethodInputs.css';
import { useOrgOptions } from '../../provider/OrganizationOptionsProvider';
import { calculateFitpassValue } from '../../../utils/calculateFitpassValue';

function PaymentMethodInputs({
    maxTotalValue = 100000.0,
    value,
    strictMode,
    onInput,
    showChangeCalculator = true,
    hideFitpass = false,
}) {
    const { fitpass } = useOrgOptions();

    const showFitpass = useMemo(() => !hideFitpass && !!fitpass, [hideFitpass, fitpass]);

    const [payments, setPayments] = useState({
        cash: parseFloat(value?.cash ? value.cash : 0),
        credit: parseFloat(value?.credit ? value.credit : 0),
        fitpass: parseFloat(value?.fitpass ? value.fitpass : 0),
    });

    const parsedMaxTotalValue = useMemo(() => {
        return Math.max(
            parseFloat(maxTotalValue) -
                calculateFitpassValue(fitpass?.$numberDecimal, payments.fitpass),
            0
        );
    }, [maxTotalValue, payments.fitpass, fitpass]);

    useEffect(() => {
        if (strictMode) {
            if (payments.credit === 0) {
                setPayments((curState) => ({ ...curState, cash: parsedMaxTotalValue, credit: 0 }));
            } else if (payments.cash === 0) {
                setPayments((curState) => ({ ...curState, cash: 0, credit: parsedMaxTotalValue }));
            } else {
                setPayments((curState) => ({ ...curState, cash: parsedMaxTotalValue, credit: 0 }));
            }
        } else {
            setPayments((curState) => {
                return {
                    cash: Math.min(parseFloat(curState.cash), parsedMaxTotalValue),
                    credit: Math.min(parseFloat(curState.credit), parsedMaxTotalValue),
                    fitpass: parseFloat(curState.fitpass),
                };
            });
        }
    }, [parsedMaxTotalValue, strictMode]); // eslint-disable-line

    const handleChange = useCallback(
        (e) => {
            const changedField = e.target.name;
            const secondField = changedField === 'cash' ? 'credit' : 'cash';

            const eventValue = parseFloat(e.target.value?.replace(/^0+/, ''));

            const newValue =
                eventValue > parsedMaxTotalValue
                    ? parsedMaxTotalValue
                    : eventValue
                      ? eventValue
                      : 0;

            setPayments((curState) => {
                return {
                    ...curState,
                    [changedField]: newValue,
                    [secondField]: strictMode
                        ? parsedMaxTotalValue - newValue
                        : curState[secondField],
                };
            });
        },
        [parsedMaxTotalValue, strictMode]
    );

    const handleFitpassChange = useCallback((e) => {
        const eventValue = parseFloat(e.target.value);

        setPayments((curState) => {
            return {
                ...curState,
                fitpass: eventValue,
            };
        });
    }, []);

    const handleModifyAmountKeyDown = useCallback((e) => {
        if (e.key === '-' || e.key === 'e') {
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        onInput(payments);
    }, [payments, onInput]);

    return (
        <>
            <div className="paymentTypeInputs">
                {showFitpass ? (
                    <div className="paymentMethodInput">
                        <label htmlFor="fitpass" className="paymentTypeLabel">
                            FITPASS
                        </label>

                        <select
                            className="fitpassInput"
                            value={payments.fitpass}
                            name="fitpass"
                            id="fitpass"
                            onChange={handleFitpassChange}>
                            <option value="0">0</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8</option>
                            <option value="9">9</option>
                            <option value="10">10</option>
                        </select>
                    </div>
                ) : null}
                <div className="paymentMethodInput">
                    <label htmlFor="cash" className="paymentTypeLabel">
                        CASH <LocalAtm className="paymentTypeIcon" />
                    </label>
                    <input
                        type="number"
                        id="cash"
                        name="cash"
                        value={payments.cash.toString()}
                        onChange={handleChange}
                        className="payment-input"
                        onKeyDown={handleModifyAmountKeyDown}
                    />
                </div>
                <div className="paymentMethodInput">
                    <label htmlFor="credit" className="paymentTypeLabel">
                        CREDIT <CreditCard className="paymentTypeIcon" />
                    </label>
                    <input
                        type="number"
                        id="credit"
                        name="credit"
                        value={payments.credit.toString()}
                        onChange={handleChange}
                        onKeyDown={handleModifyAmountKeyDown}
                        className="payment-input"
                    />
                    {showChangeCalculator ? <ChangeCalculator priceToPay={payments.cash} /> : null}
                </div>
            </div>
        </>
    );
}

export default PaymentMethodInputs;

